/**
 * Self-healing debug layer — runs silently in the background.
 * Catches unhandled errors, promise rejections, and audio failures,
 * then applies automatic recovery strategies.
 *
 * Call `initSelfHeal()` once at app boot (main.tsx).
 */

const HEAL_LOG_KEY = "llf_heal_log";
const MAX_LOG_ENTRIES = 50;

interface HealEntry {
  ts: number;
  type: string;
  msg: string;
  action: string;
}

function log(entry: Omit<HealEntry, "ts">) {
  try {
    const raw = localStorage.getItem(HEAL_LOG_KEY);
    const arr: HealEntry[] = raw ? JSON.parse(raw) : [];
    arr.push({ ...entry, ts: Date.now() });
    // Keep only the last N entries
    if (arr.length > MAX_LOG_ENTRIES) arr.splice(0, arr.length - MAX_LOG_ENTRIES);
    localStorage.setItem(HEAL_LOG_KEY, JSON.stringify(arr));
  } catch { /* localStorage unavailable */ }
}

/** Validate and repair a JSON localStorage key */
function healJsonKey(key: string): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return false;
    JSON.parse(raw);
    return false; // valid
  } catch {
    try { localStorage.removeItem(key); } catch { /* noop */ }
    log({ type: "heal", msg: `Removed corrupt key: ${key}`, action: "removeItem" });
    return true;
  }
}

/** Periodic localStorage integrity check */
function healStorageIntegrity() {
  const jsonKeys = [
    "llf_played_history",
    "llf_played_groups",
    "llf_implicit_groups",
  ];
  let healed = 0;
  for (const k of jsonKeys) {
    if (healJsonKey(k)) healed++;
  }

  // Check quota — if near limit, prune old stats keys
  try {
    const test = "__quota_test__";
    localStorage.setItem(test, "x".repeat(1024));
    localStorage.removeItem(test);
  } catch {
    // Quota exceeded — prune stats & reaction keys
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith("llf_stats_") || k.startsWith("llf_react_"))) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) {
      try { localStorage.removeItem(k); } catch { /* noop */ }
    }
    if (toRemove.length > 0) {
      log({ type: "heal", msg: `Pruned ${toRemove.length} stat keys (quota)`, action: "prune" });
    }
    healed += toRemove.length;
  }

  return healed;
}

/** Monitor audio elements for stalled/error states and auto-recover */
function watchAudioHealth() {
  // Periodically check for stuck audio elements
  setInterval(() => {
    const audios = document.querySelectorAll("audio");
    audios.forEach((audio) => {
      // Detect stalled state (waiting > 15s with no progress)
      if (audio.readyState < 3 && !audio.paused && audio.currentTime === 0) {
        const src = audio.src;
        if (src && !src.startsWith("data:")) {
          log({ type: "audio", msg: `Stalled audio detected`, action: "reload" });
          // Force reload the source
          const currentSrc = audio.src;
          audio.src = "";
          setTimeout(() => {
            audio.src = currentSrc;
            audio.play().catch(() => {});
          }, 500);
        }
      }
    });
  }, 20000); // check every 20s
}

/** Catch memory pressure on mobile and free resources */
function watchMemoryPressure() {
  // Some mobile browsers fire this when memory is low
  if ("onmemorywarning" in window) {
    (window as any).addEventListener("memorywarning", () => {
      log({ type: "memory", msg: "Memory warning received", action: "gc" });
      // Clear caches
      try { localStorage.removeItem("llf_implicit_groups"); } catch { /* noop */ }
    });
  }

  // Listen for page visibility — clean up when backgrounded on mobile
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      // Free preload cache to reduce memory while backgrounded
      try { localStorage.removeItem("llf_implicit_groups"); } catch { /* noop */ }
    }
  });
}

export function initSelfHeal() {
  // 1. Global unhandled error handler
  window.addEventListener("error", (e) => {
    log({
      type: "error",
      msg: `${e.message} at ${e.filename}:${e.lineno}`,
      action: "logged",
    });

    // If it's a chunk load failure, reload once
    if (e.message?.includes("Loading chunk") || e.message?.includes("Failed to fetch dynamically")) {
      log({ type: "error", msg: "Chunk load failure — reloading", action: "reload" });
      const reloaded = sessionStorage.getItem("llf_chunk_reload");
      if (!reloaded) {
        sessionStorage.setItem("llf_chunk_reload", "1");
        window.location.reload();
      }
    }
  });

  // 2. Unhandled promise rejections
  window.addEventListener("unhandledrejection", (e) => {
    const msg = e.reason?.message || String(e.reason);
    log({ type: "rejection", msg, action: "logged" });

    // Suppress benign mobile errors (play() interrupted, AbortError)
    if (
      msg.includes("play() request was interrupted") ||
      msg.includes("AbortError") ||
      msg.includes("user aborted")
    ) {
      e.preventDefault(); // prevent console noise
    }
  });

  // 3. Initial storage heal
  healStorageIntegrity();

  // 4. Periodic storage check (every 60s)
  setInterval(() => healStorageIntegrity(), 60000);

  // 5. Audio health monitor
  watchAudioHealth();

  // 6. Memory pressure handler
  watchMemoryPressure();

  // Clear chunk-reload flag on successful boot
  try { sessionStorage.removeItem("llf_chunk_reload"); } catch { /* noop */ }

  console.info("[SelfHeal] Initialized — background health monitoring active");
}
